/**
 * Système d'analytics et tracking pour ATOM Group
 * Mesure les conversions, engagement et performance business
 */

// Configuration des événements de tracking
export const TRACKING_EVENTS = {
  // Engagement utilisateur
  PAGE_VIEW: 'page_view',
  SCROLL_DEPTH: 'scroll_depth',
  TIME_ON_PAGE: 'time_on_page',
  
  // Actions de conversion
  CONTACT_FORM_START: 'contact_form_start',
  CONTACT_FORM_SUBMIT: 'contact_form_submit',
  CONTACT_FORM_SUCCESS: 'contact_form_success',
  CONTACT_FORM_ERROR: 'contact_form_error',
  
  // Navigation et engagement
  CTA_CLICK: 'cta_click',
  SERVICE_PAGE_VIEW: 'service_page_view',
  CALCULATOR_USE: 'calculator_use',
  DOWNLOAD_GUIDE: 'download_guide',
  
  // Interactions téléphone/email
  PHONE_CLICK: 'phone_click',
  EMAIL_CLICK: 'email_click',
  
  // Features spécifiques
  WEATHER_WIDGET_INTERACT: 'weather_widget_interact',
  FAQ_EXPAND: 'faq_expand',
  TESTIMONIAL_NAVIGATE: 'testimonial_navigate'
};

/**
 * Envoyer un événement à Google Analytics 4
 */
export const trackGA4Event = (eventName, parameters = {}) => {
  if (typeof gtag !== 'undefined') {
    gtag('event', eventName, {
      event_category: parameters.category || 'engagement',
      event_label: parameters.label,
      value: parameters.value,
      custom_parameter: parameters.custom_parameter,
      page_title: document.title,
      page_location: window.location.href,
      ...parameters
    });
  }
};

/**
 * Envoyer un événement à PostHog
 */
export const trackPostHogEvent = (eventName, properties = {}) => {
  if (typeof posthog !== 'undefined') {
    posthog.capture(eventName, {
      $current_url: window.location.href,
      $timestamp: new Date().toISOString(),
      page_title: document.title,
      ...properties
    });
  }
};

/**
 * Fonction principale de tracking (envoie à tous les services)
 */
export const track = (eventName, parameters = {}) => {
  // Validation
  if (!eventName) {
    console.warn('Analytics: Event name is required');
    return;
  }
  
  // Log en développement
  if (process.env.NODE_ENV === 'development') {
    console.log(`📊 Analytics Event: ${eventName}`, parameters);
  }
  
  // Envoyer à GA4
  trackGA4Event(eventName, parameters);
  
  // Envoyer à PostHog
  trackPostHogEvent(eventName, parameters);
  
  // Envoyer au backend pour analytics internes (optionnel)
  if (parameters.sendToBackend) {
    sendToBackend(eventName, parameters);
  }
};

/**
 * Envoyer les événements au backend pour analytics internes
 */
const sendToBackend = async (eventName, parameters) => {
  try {
    const response = await fetch(`/api/analytics/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event: eventName,
        parameters,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to send analytics to backend');
    }
  } catch (error) {
    console.warn('Analytics backend error:', error);
  }
};

/**
 * Tracker de profondeur de scroll
 */
export const setupScrollTracking = () => {
  let scrollDepths = [25, 50, 75, 100];
  let trackedDepths = [];
  
  const handleScroll = () => {
    const scrollTop = window.pageYOffset;
    const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((scrollTop / documentHeight) * 100);
    
    scrollDepths.forEach(depth => {
      if (scrollPercent >= depth && !trackedDepths.includes(depth)) {
        trackedDepths.push(depth);
        track(TRACKING_EVENTS.SCROLL_DEPTH, {
          category: 'engagement',
          label: `${depth}%`,
          value: depth,
          page: window.location.pathname
        });
      }
    });
  };
  
  // Throttle pour performance
  let scrollTimeout;
  window.addEventListener('scroll', () => {
    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(handleScroll, 100);
  });
};

/**
 * Tracker de temps passé sur la page
 */
export const setupTimeTracking = () => {
  const startTime = Date.now();
  let timeIntervals = [30, 60, 120, 300]; // 30s, 1min, 2min, 5min
  let trackedIntervals = [];
  
  const trackTime = () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    
    timeIntervals.forEach(interval => {
      if (timeSpent >= interval && !trackedIntervals.includes(interval)) {
        trackedIntervals.push(interval);
        track(TRACKING_EVENTS.TIME_ON_PAGE, {
          category: 'engagement',
          label: `${interval}s`,
          value: interval,
          page: window.location.pathname
        });
      }
    });
  };
  
  setInterval(trackTime, 10000); // Vérifier toutes les 10 secondes
  
  // Tracker à la fermeture de la page
  window.addEventListener('beforeunload', () => {
    const totalTime = Math.round((Date.now() - startTime) / 1000);
    track(TRACKING_EVENTS.TIME_ON_PAGE, {
      category: 'engagement',
      label: 'total_time',
      value: totalTime,
      page: window.location.pathname
    });
  });
};

/**
 * Tracker spécifique pour les formulaires de contact
 */
export const trackContactForm = {
  start: (formType = 'contact') => {
    track(TRACKING_EVENTS.CONTACT_FORM_START, {
      category: 'conversion',
      label: formType,
      form_type: formType
    });
  },
  
  submit: (formType = 'contact', formData = {}) => {
    track(TRACKING_EVENTS.CONTACT_FORM_SUBMIT, {
      category: 'conversion',
      label: formType,
      form_type: formType,
      theme: formData.theme,
      has_message: !!formData.message,
      sendToBackend: true
    });
  },
  
  success: (formType = 'contact') => {
    track(TRACKING_EVENTS.CONTACT_FORM_SUCCESS, {
      category: 'conversion',
      label: formType,
      form_type: formType,
      value: 1, // Conversion value
      sendToBackend: true
    });
  },
  
  error: (formType = 'contact', error = '') => {
    track(TRACKING_EVENTS.CONTACT_FORM_ERROR, {
      category: 'conversion',
      label: formType,
      form_type: formType,
      error_message: error
    });
  }
};

/**
 * Tracker pour les CTA (Call-To-Action)
 */
export const trackCTA = (ctaName, location, destination = '') => {
  track(TRACKING_EVENTS.CTA_CLICK, {
    category: 'navigation',
    label: ctaName,
    cta_name: ctaName,
    cta_location: location,
    destination: destination
  });
};

/**
 * Tracker pour les pages de services
 */
export const trackServicePage = (serviceName) => {
  track(TRACKING_EVENTS.SERVICE_PAGE_VIEW, {
    category: 'content',
    label: serviceName,
    service_name: serviceName
  });
};

/**
 * Tracker pour les calculateurs
 */
export const trackCalculator = (calculatorType, values = {}) => {
  track(TRACKING_EVENTS.CALCULATOR_USE, {
    category: 'tools',
    label: calculatorType,
    calculator_type: calculatorType,
    ...values
  });
};

/**
 * Setup complet du tracking
 */
export const initializeAnalytics = () => {
  // Setup des trackers automatiques
  setupScrollTracking();
  setupTimeTracking();
  
  // Tracker les clics sur téléphone et email
  document.addEventListener('click', (e) => {
    const target = e.target.closest('a');
    if (!target) return;
    
    const href = target.getAttribute('href');
    if (!href) return;
    
    if (href.startsWith('tel:')) {
      track(TRACKING_EVENTS.PHONE_CLICK, {
        category: 'contact',
        label: 'phone_number',
        phone_number: href.replace('tel:', '')
      });
    } else if (href.startsWith('mailto:')) {
      track(TRACKING_EVENTS.EMAIL_CLICK, {
        category: 'contact',
        label: 'email',
        email: href.replace('mailto:', '')
      });
    }
  });
  
  // Tracker les téléchargements de guides
  document.addEventListener('click', (e) => {
    const target = e.target.closest('[data-download]');
    if (target) {
      const guideName = target.getAttribute('data-download');
      track(TRACKING_EVENTS.DOWNLOAD_GUIDE, {
        category: 'content',
        label: guideName,
        guide_name: guideName
      });
    }
  });
  
  console.log('📊 Analytics initialized');
};

export default {
  track,
  trackContactForm,
  trackCTA,
  trackServicePage,
  trackCalculator,
  initializeAnalytics,
  TRACKING_EVENTS
};